import { LogLevel, Logger } from "@hiddentao/logger"
import type { LogEntry, Transport } from "@hiddentao/logger"

// Custom console transport for browser
class ConsoleTransport implements Transport {
  write(entry: LogEntry): void {
    const { level, message, messageParts, category } = entry

    // Define color styles for different log levels
    const logStyles: Record<string, string> = {
      [LogLevel.DEBUG]: "color: #6c757d", // Grey
      [LogLevel.INFO]: "color: #0d6efd", // Blue
      [LogLevel.WARN]: "color: #fd7e14", // Orange
      [LogLevel.ERROR]: "color: #dc3545", // Red
    }

    const levelNames: Record<string, string> = {
      [LogLevel.DEBUG]: "DEBUG",
      [LogLevel.INFO]: "INFO",
      [LogLevel.WARN]: "WARN",
      [LogLevel.ERROR]: "ERROR",
    }

    const prefix = `%c[${levelNames[level] || level}]`
    const categoryPrefix = category ? ` [${category}]` : ""
    const formattedMessage = `${prefix}${categoryPrefix} ${message}`
    const style = logStyles[level] || "color: #212529" // Default to dark grey if level not found

    // Extract meta object if it exists (last item in messageParts if it's an object)
    const lastPart = messageParts[messageParts.length - 1]
    const meta =
      typeof lastPart === "object" && lastPart !== null ? lastPart : undefined

    const logData = meta
      ? [formattedMessage, style, meta]
      : [formattedMessage, style]

    switch (level) {
      case LogLevel.DEBUG:
        console.debug(...logData)
        break
      case LogLevel.INFO:
        console.info(...logData)
        break
      case LogLevel.WARN:
        console.warn(...logData)
        break
      case LogLevel.ERROR:
        console.error(...logData)
        break
      default:
        console.log(...logData)
    }
  }
}

// Create the root logger
export const logger = new Logger({
  minLevel: LogLevel.DEBUG,
})

// Add browser console transport
logger.addTransport(new ConsoleTransport())

// Create loggers with categories
export const createLogger = (category: string) => {
  return logger.child(category)
}
